import { useState, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@trafiflow/shared-ui'
import {
  X,
  FileText,
  History,
  PlayCircle,
  Activity,
  Save,
  RotateCcw,
  Loader2,
  AlertCircle,
  Check,
  Clock,
  ChevronDown,
  ChevronUp,
} from 'lucide-react'
import { promptEngineeringService } from '@/services/prompt-engineering'
import type {
  PromptResponse,
  TestPromptResponse,
  ExecutionResponse,
} from '@/services/prompt-engineering'
import type { AgentDefinition } from './AgentArchitectureGraph'

type TabType = 'editor' | 'history' | 'playground' | 'executions'

interface AgentNodeModalProps {
  agent: AgentDefinition
  prompts: PromptResponse[]
  isOpen: boolean
  onClose: () => void
  onSave?: () => void
}

export default function AgentNodeModal({
  agent,
  prompts,
  isOpen,
  onClose,
  onSave,
}: AgentNodeModalProps) {
  // Find the first matching prompt for this agent
  const matchingPrompts = prompts.filter((p) => agent.prompts.includes(p.prompt_id))
  const [selectedPrompt, setSelectedPrompt] = useState<PromptResponse | null>(
    matchingPrompts[0] || null
  )

  const [activeTab, setActiveTab] = useState<TabType>('editor')
  const [versions, setVersions] = useState<PromptResponse[]>([])

  // Editor state
  const [systemPrompt, setSystemPrompt] = useState('')
  const [userPromptTemplate, setUserPromptTemplate] = useState('')
  const [model, setModel] = useState('')
  const [description, setDescription] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)

  // Playground state
  const [variables, setVariables] = useState<Record<string, string>>({})
  const [testResult, setTestResult] = useState<TestPromptResponse | null>(null)
  const [isTesting, setIsTesting] = useState(false)

  // Executions state
  const [executions, setExecutions] = useState<ExecutionResponse[]>([])
  const [loadingExecutions, setLoadingExecutions] = useState(false)
  const [expandedExecution, setExpandedExecution] = useState<string | null>(null)

  // Reset state when agent changes
  useEffect(() => {
    const newMatchingPrompts = prompts.filter((p) => agent.prompts.includes(p.prompt_id))
    setSelectedPrompt(newMatchingPrompts[0] || null)
    setActiveTab('editor')
    setTestResult(null)
    setExpandedExecution(null)
  }, [agent, prompts])

  // Load versions when prompt is selected
  const fetchVersions = useCallback(async (promptId: string) => {
    try {
      const response = await promptEngineeringService.getPromptVersions(promptId)
      setVersions(response.versions || [])
    } catch (err) {
      console.error('Failed to load versions:', err)
    }
  }, [])

  // Load executions for selected prompt
  const fetchExecutions = useCallback(async (promptId: string) => {
    try {
      setLoadingExecutions(true)
      const response = await promptEngineeringService.listPromptExecutions(promptId, 50)
      setExecutions(response.executions || [])
    } catch (err) {
      console.error('Failed to load executions:', err)
    } finally {
      setLoadingExecutions(false)
    }
  }, [])

  // When prompt is selected, load its data
  useEffect(() => {
    if (selectedPrompt) {
      setSystemPrompt(selectedPrompt.system_prompt)
      setUserPromptTemplate(selectedPrompt.user_prompt_template)
      setModel(selectedPrompt.model)
      setDescription(selectedPrompt.description)
      setHasChanges(false)

      // Initialize variables
      const vars: Record<string, string> = {}
      selectedPrompt.variables.forEach((v) => {
        vars[v] = ''
      })
      setVariables(vars)

      // Load versions and executions
      fetchVersions(selectedPrompt.prompt_id)
      fetchExecutions(selectedPrompt.prompt_id)
    }
  }, [selectedPrompt, fetchVersions, fetchExecutions])

  // Track changes
  useEffect(() => {
    if (selectedPrompt) {
      const changed =
        systemPrompt !== selectedPrompt.system_prompt ||
        userPromptTemplate !== selectedPrompt.user_prompt_template ||
        model !== selectedPrompt.model ||
        description !== selectedPrompt.description
      setHasChanges(changed)
    }
  }, [systemPrompt, userPromptTemplate, model, description, selectedPrompt])

  // Save new version
  const handleSaveVersion = async () => {
    if (!selectedPrompt || !hasChanges) return

    try {
      setIsSaving(true)
      const newVersion = await promptEngineeringService.createPromptVersion(
        selectedPrompt.prompt_id,
        {
          system_prompt: systemPrompt,
          user_prompt_template: userPromptTemplate,
          model,
          description,
        }
      )
      setSelectedPrompt(newVersion)
      setHasChanges(false)
      await fetchVersions(selectedPrompt.prompt_id)
      onSave?.()
    } catch (err) {
      console.error('Failed to save version:', err)
    } finally {
      setIsSaving(false)
    }
  }

  // Activate version
  const handleActivateVersion = async (version: number) => {
    if (!selectedPrompt) return

    try {
      await promptEngineeringService.activatePromptVersion(selectedPrompt.prompt_id, version)
      await fetchVersions(selectedPrompt.prompt_id)
      const activatedVersion = versions.find((v) => v.version === version)
      if (activatedVersion) {
        setSelectedPrompt({ ...activatedVersion, is_active: true })
      }
      onSave?.()
    } catch (err) {
      console.error('Failed to activate version:', err)
    }
  }

  // Test prompt
  const handleTestPrompt = async () => {
    if (!selectedPrompt) return

    try {
      setIsTesting(true)
      setTestResult(null)
      const result = await promptEngineeringService.testPrompt(selectedPrompt.prompt_id, {
        variables,
      })
      setTestResult(result)
    } catch (err) {
      setTestResult({
        system_prompt: '',
        user_prompt: '',
        raw_output: '',
        duration_ms: 0,
        status: 'failed',
        error: err instanceof Error ? err.message : 'Test failed',
      })
    } finally {
      setIsTesting(false)
    }
  }

  // Handle reset
  const handleReset = () => {
    if (selectedPrompt) {
      setSystemPrompt(selectedPrompt.system_prompt)
      setUserPromptTemplate(selectedPrompt.user_prompt_template)
      setModel(selectedPrompt.model)
      setDescription(selectedPrompt.description)
    }
  }

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    if (isOpen) {
      window.addEventListener('keydown', handleEscape)
      return () => window.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  const modalContent = (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-4xl max-h-[90vh] bg-white rounded-xl shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-3">
            <div
              className={cn(
                'w-10 h-10 rounded-lg flex items-center justify-center',
                agent.category === 'orchestrator' && 'bg-purple-100',
                agent.category === 'onboarding' && 'bg-blue-100',
                agent.category === 'storyboard' && 'bg-emerald-100',
                agent.category === 'swiper' && 'bg-amber-100',
                agent.category === 'knowledge' && 'bg-rose-100'
              )}
            >
              <agent.icon
                className={cn(
                  'w-5 h-5',
                  agent.category === 'orchestrator' && 'text-purple-600',
                  agent.category === 'onboarding' && 'text-blue-600',
                  agent.category === 'storyboard' && 'text-emerald-600',
                  agent.category === 'swiper' && 'text-amber-600',
                  agent.category === 'knowledge' && 'text-rose-600'
                )}
              />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{agent.label}</h2>
              <p className="text-sm text-gray-500">{agent.description}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Prompt selector (if multiple prompts) */}
        {matchingPrompts.length > 1 && (
          <div className="px-6 py-3 border-b border-gray-200 bg-white">
            <label className="text-xs font-medium text-gray-500 mb-1 block">Select Prompt</label>
            <select
              value={selectedPrompt?.prompt_id || ''}
              onChange={(e) => {
                const prompt = matchingPrompts.find((p) => p.prompt_id === e.target.value)
                if (prompt) setSelectedPrompt(prompt)
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              {matchingPrompts.map((p) => (
                <option key={p.prompt_id} value={p.prompt_id}>
                  {p.name} (v{p.version})
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Tabs */}
        {selectedPrompt ? (
          <>
            <div className="flex items-center gap-1 px-6 py-2 border-b border-gray-200 bg-white">
              <TabButton
                active={activeTab === 'editor'}
                onClick={() => setActiveTab('editor')}
                icon={<FileText className="w-4 h-4" />}
                label="Editor"
              />
              <TabButton
                active={activeTab === 'history'}
                onClick={() => setActiveTab('history')}
                icon={<History className="w-4 h-4" />}
                label="History"
              />
              <TabButton
                active={activeTab === 'playground'}
                onClick={() => setActiveTab('playground')}
                icon={<PlayCircle className="w-4 h-4" />}
                label="Playground"
              />
              <TabButton
                active={activeTab === 'executions'}
                onClick={() => setActiveTab('executions')}
                icon={<Activity className="w-4 h-4" />}
                label="Executions"
              />
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
              {activeTab === 'editor' && (
                <EditorContent
                  selectedPrompt={selectedPrompt}
                  systemPrompt={systemPrompt}
                  setSystemPrompt={setSystemPrompt}
                  userPromptTemplate={userPromptTemplate}
                  setUserPromptTemplate={setUserPromptTemplate}
                  model={model}
                  setModel={setModel}
                  description={description}
                  setDescription={setDescription}
                  hasChanges={hasChanges}
                  isSaving={isSaving}
                  onSave={handleSaveVersion}
                  onReset={handleReset}
                />
              )}

              {activeTab === 'history' && (
                <HistoryContent
                  versions={versions}
                  currentVersion={selectedPrompt.version}
                  onActivate={handleActivateVersion}
                  onSelect={(v) => {
                    setSystemPrompt(v.system_prompt)
                    setUserPromptTemplate(v.user_prompt_template)
                    setModel(v.model)
                    setDescription(v.description)
                  }}
                />
              )}

              {activeTab === 'playground' && (
                <PlaygroundContent
                  variables={variables}
                  setVariables={setVariables}
                  expectedVariables={selectedPrompt.variables}
                  testResult={testResult}
                  isTesting={isTesting}
                  onTest={handleTestPrompt}
                />
              )}

              {activeTab === 'executions' && (
                <ExecutionsContent
                  executions={executions}
                  loading={loadingExecutions}
                  expandedExecution={expandedExecution}
                  setExpandedExecution={setExpandedExecution}
                />
              )}
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center p-6 bg-gray-50">
            <div className="text-center text-gray-500">
              <AlertCircle className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-medium">No prompts configured</p>
              <p className="text-sm">This agent doesn't have any prompts associated with it.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  )

  return createPortal(modalContent, document.body)
}

// ──────────────────────────────────────────────────────────────────────────────
// Sub-components
// ──────────────────────────────────────────────────────────────────────────────

function TabButton({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean
  onClick: () => void
  icon: React.ReactNode
  label: string
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors',
        active
          ? 'bg-blue-100 text-blue-700'
          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
      )}
    >
      {icon}
      {label}
    </button>
  )
}

function EditorContent({
  selectedPrompt,
  systemPrompt,
  setSystemPrompt,
  userPromptTemplate,
  setUserPromptTemplate,
  model,
  setModel,
  description,
  setDescription,
  hasChanges,
  isSaving,
  onSave,
  onReset,
}: {
  selectedPrompt: PromptResponse
  systemPrompt: string
  setSystemPrompt: (v: string) => void
  userPromptTemplate: string
  setUserPromptTemplate: (v: string) => void
  model: string
  setModel: (v: string) => void
  description: string
  setDescription: (v: string) => void
  hasChanges: boolean
  isSaving: boolean
  onSave: () => void
  onReset: () => void
}) {
  return (
    <div className="space-y-4">
      {/* Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {hasChanges && (
            <span className="text-sm text-amber-600 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              Unsaved changes
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onReset}
            disabled={!hasChanges}
            className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50 flex items-center gap-1"
          >
            <RotateCcw className="w-4 h-4" />
            Reset
          </button>
          <button
            onClick={onSave}
            disabled={!hasChanges || isSaving}
            className="px-4 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            {isSaving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Save New Version
          </button>
        </div>
      </div>

      {/* Model & Description */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
            <select
              value={model}
              onChange={(e) => setModel(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <optgroup label="Gemini 2.5">
                <option value="gemini-2.5-pro">gemini-2.5-pro</option>
                <option value="gemini-2.5-flash">gemini-2.5-flash</option>
                <option value="gemini-2.5-flash-lite">gemini-2.5-flash-lite</option>
              </optgroup>
              <optgroup label="Gemini 2.0">
                <option value="gemini-2.0-flash">gemini-2.0-flash</option>
                <option value="gemini-2.0-flash-lite">gemini-2.0-flash-lite</option>
              </optgroup>
              <optgroup label="Gemini 1.5">
                <option value="gemini-1.5-pro">gemini-1.5-pro</option>
                <option value="gemini-1.5-flash">gemini-1.5-flash</option>
              </optgroup>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              placeholder="Brief description..."
            />
          </div>
        </div>
      </div>

      {/* Variables */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="text-sm font-medium text-gray-700 mb-2">Expected Variables</h3>
        <div className="flex flex-wrap gap-2">
          {selectedPrompt.variables.map((v) => (
            <code
              key={v}
              className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded font-mono border border-blue-200"
            >
              {`{{${v}}}`}
            </code>
          ))}
        </div>
      </div>

      {/* System Prompt */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">System Prompt</label>
        <textarea
          value={systemPrompt}
          onChange={(e) => setSystemPrompt(e.target.value)}
          rows={8}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono resize-y"
          placeholder="System prompt..."
        />
      </div>

      {/* User Prompt Template */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          User Prompt Template
        </label>
        <textarea
          value={userPromptTemplate}
          onChange={(e) => setUserPromptTemplate(e.target.value)}
          rows={8}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono resize-y"
          placeholder="User prompt template with {{variables}}..."
        />
      </div>
    </div>
  )
}

function HistoryContent({
  versions,
  currentVersion,
  onActivate,
  onSelect,
}: {
  versions: PromptResponse[]
  currentVersion: number
  onActivate: (version: number) => void
  onSelect: (version: PromptResponse) => void
}) {
  return (
    <div className="space-y-2">
      {versions.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-8">No version history</p>
      ) : (
        versions.map((v) => (
          <div
            key={v.version}
            className={cn(
              'bg-white rounded-lg border p-4 cursor-pointer hover:bg-gray-50',
              v.version === currentVersion ? 'border-blue-500 ring-1 ring-blue-500' : 'border-gray-200'
            )}
            onClick={() => onSelect(v)}
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">Version {v.version}</span>
                  {v.is_active && (
                    <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                      Active
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-500 mt-1">{v.description || 'No description'}</p>
                <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {new Date(v.created_at).toLocaleString()}
                  </span>
                  <span>by {v.created_by}</span>
                </div>
              </div>
              {!v.is_active && (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    onActivate(v.version)
                  }}
                  className="px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg"
                >
                  Activate
                </button>
              )}
            </div>
          </div>
        ))
      )}
    </div>
  )
}

function PlaygroundContent({
  variables,
  setVariables,
  expectedVariables,
  testResult,
  isTesting,
  onTest,
}: {
  variables: Record<string, string>
  setVariables: (v: Record<string, string>) => void
  expectedVariables: string[]
  testResult: TestPromptResponse | null
  isTesting: boolean
  onTest: () => void
}) {
  return (
    <div className="space-y-4">
      {/* Variables Input */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="text-sm font-medium text-gray-700 mb-4">Test Variables</h3>
        <div className="space-y-3">
          {expectedVariables.map((varName) => (
            <div key={varName}>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {`{{${varName}}}`}
              </label>
              <textarea
                value={variables[varName] || ''}
                onChange={(e) => setVariables({ ...variables, [varName]: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono resize-y"
                placeholder={`Value for ${varName}...`}
              />
            </div>
          ))}
        </div>
        <button
          onClick={onTest}
          disabled={isTesting}
          className="mt-4 w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {isTesting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Running...
            </>
          ) : (
            <>
              <PlayCircle className="w-4 h-4" />
              Run Test
            </>
          )}
        </button>
      </div>

      {/* Test Result */}
      {testResult && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-700">Result</h3>
            <div className="flex items-center gap-2">
              <span
                className={`px-2 py-0.5 text-xs rounded-full ${
                  testResult.status === 'success'
                    ? 'bg-green-100 text-green-700'
                    : 'bg-red-100 text-red-700'
                }`}
              >
                {testResult.status}
              </span>
              <span className="text-xs text-gray-500">{testResult.duration_ms}ms</span>
            </div>
          </div>

          {testResult.error ? (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {testResult.error}
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <h4 className="text-xs font-medium text-gray-500 mb-1">Raw Output</h4>
                <pre className="p-3 bg-gray-900 text-green-400 rounded-lg text-xs font-mono overflow-x-auto whitespace-pre-wrap max-h-64">
                  {testResult.raw_output}
                </pre>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function ExecutionsContent({
  executions,
  loading,
  expandedExecution,
  setExpandedExecution,
}: {
  executions: ExecutionResponse[]
  loading: boolean
  expandedExecution: string | null
  setExpandedExecution: (id: string | null) => void
}) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    )
  }

  if (executions.length === 0) {
    return <p className="text-sm text-gray-500 text-center py-8">No executions found</p>
  }

  return (
    <div className="space-y-2">
      {executions.slice(0, 20).map((exec) => (
        <div key={exec.execution_id} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <button
            onClick={() =>
              setExpandedExecution(expandedExecution === exec.execution_id ? null : exec.execution_id)
            }
            className="w-full p-4 text-left hover:bg-gray-50"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {exec.status === 'success' ? (
                  <Check className="w-4 h-4 text-green-600" />
                ) : (
                  <AlertCircle className="w-4 h-4 text-red-600" />
                )}
                <div>
                  <div className="flex items-center gap-2">
                    <span className={`px-1.5 py-0.5 text-xs rounded ${
                      exec.source === 'playground'
                        ? 'bg-gray-100 text-gray-600'
                        : exec.source === 'storyboard'
                        ? 'bg-purple-100 text-purple-700'
                        : 'bg-blue-100 text-blue-700'
                    }`}>
                      {exec.source || 'playground'}
                    </span>
                    <span className="text-xs text-gray-400">v{exec.prompt_version}</span>
                  </div>
                  <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                    <span>{new Date(exec.created_at).toLocaleString()}</span>
                    <span>{exec.duration_ms}ms</span>
                  </div>
                </div>
              </div>
              {expandedExecution === exec.execution_id ? (
                <ChevronUp className="w-4 h-4 text-gray-400" />
              ) : (
                <ChevronDown className="w-4 h-4 text-gray-400" />
              )}
            </div>
          </button>

          {expandedExecution === exec.execution_id && (
            <div className="border-t border-gray-200 p-4 bg-gray-50 space-y-4">
              {exec.error_message && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  {exec.error_message}
                </div>
              )}

              <div>
                <h4 className="text-xs font-medium text-gray-500 mb-1">Input Variables</h4>
                <pre className="p-3 bg-gray-100 rounded-lg text-xs font-mono overflow-x-auto">
                  {JSON.stringify(exec.input_variables, null, 2)}
                </pre>
              </div>

              <div>
                <h4 className="text-xs font-medium text-gray-500 mb-1">Raw Output</h4>
                <pre className="p-3 bg-gray-900 text-green-400 rounded-lg text-xs font-mono overflow-x-auto whitespace-pre-wrap max-h-48">
                  {exec.raw_output}
                </pre>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}
